import { useState } from "react";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import ContinueButton from "@components/ContinueButton";
import useContent from "@lib/use_content";
import { FormProvider, useForm } from "react-hook-form";
import Email from "@hudson/ui/components/FormFields/Email";
import Error from "@hudson/ui/components/Error";
import Name from "./shared/Name";
import { useRouter } from "next/router";
import NewPassword from "./shared/NewPassword";
import Button from "@hudson/ui/components/FormFields/Button";
import profileAdd from "@services/profile_add";
import PasswordStrengthBar from "react-password-strength-bar";

interface NewUserProps {
  onNewUser?: () => void;
  onPageChange: (page: string) => void;
}

function NewUser({ onNewUser, onPageChange }: NewUserProps) {
  const router = useRouter();
  const { data: cData, loading } = useContent([
    "beErrEmailFormat",
    "beErrFieldRequired",
    "beErrProfileUpdate",
    "beNewProfileBackButton",
    "beNewProfileSubmitButton",
    "beNewProfileTitle",
    "beProfileEmailLabel",
    "beProfileNameLabel",
    "beProfilePasswordLabel",
    "beRequiredPlaceholder",
  ]);
  const formMethods = useForm({});
  const [errorMessage, setErrorMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const inputPassword = formMethods.watch("password");

  interface NewData {
    email: string;
    name?: string;
    password: string;
  }

  async function submitForm(data: NewData) {
    setErrorMessage("");
    setSubmitting(true);

    const { error, profileId } = await profileAdd(data);

    if (!profileId) {
      setErrorMessage(error || cData?.content.beErrProfileUpdate);
    } else {
      if (onNewUser) onNewUser();
      else router.push(`/${router.query.siteId}?open_profile=1`);
    }

    setSubmitting(false);
  }

  return (
    <>
      {loading ? (
        <ConcentricLoader className="opacity-60 max-w-2xl mx-auto  px-4 sm:py-10 sm:px-6 lg:px-8" />
      ) : (
        <FormProvider {...formMethods}>
          <form
            className=" flex flex-col items-center gap-y-6 sm:gap-x-4 mx-auto max-w-xs m-6"
            id="new-user"
            onSubmit={(e) => {
              e.preventDefault();
              formMethods.handleSubmit(submitForm)();
            }}
            noValidate
          >
            {onPageChange && (
              <div className="mb-4 self-start">
                <Button type="button" onClick={() => onPageChange("loginOrCreate")} asLink>
                  {cData?.content.beNewProfileBackButton}
                </Button>
              </div>
            )}

            <h2 className="mb-3 text-3xl md:text-3xl font-extrabold flex items-center flex-wrap opacity-70">
              {cData?.content.beNewProfileTitle}
            </h2>

            {errorMessage && <Error className="mt-2" message={errorMessage} heavyStyle scrollTo />}

            <>
              <Name
                autoFocus
                errorMessage={cData?.content.beErrFieldRequired}
                fieldName="name"
                label={cData.content.beProfileNameLabel}
                requiredPlaceholder={cData?.content.beRequiredPlaceholder}
              />

              <Email
                errorMessage={cData?.content.beErrFieldRequired}
                errorFormatMessage={cData?.content.beErrEmailFormat}
                fieldName="email"
                label={cData.content.beProfileEmailLabel}
                requiredPlaceholder={cData?.content.beRequiredPlaceholder}
                widthClass="w-64"
              />

              <NewPassword
                errorMessage={cData?.content.beErrFieldRequired}
                fieldName="password"
                label={cData?.content.beProfilePasswordLabel}
                requiredPlaceholder={cData?.content.beRequiredPlaceholder}
              />
              <PasswordStrengthBar className="-mt-4 w-64" minLength={8} password={inputPassword} />

              <ContinueButton
                alignLeft
                className="justify-self-start mt-2"
                disabled={submitting}
                label={cData?.content.beNewProfileSubmitButton}
                loading={submitting}
              />
            </>
          </form>
        </FormProvider>
      )}
    </>
  );
}

export default NewUser;
